import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslation } from '@zurich/common/hooks';
import { InfoSummary } from '~/components/Steps/InfoSummary';
import type { SummaryConfigType, SummaryDataType } from '~/lib/types';
import {
  activeStepSelector,
  confirmationOfCorrectPhotoDataSelector,
  confirmationOfCorrectPhotoDataValidationSelector,
  numberOfDamagesPerStepSelector,
} from '~/state';

export function PhotosSummary() {
  const { t } = useTranslation();
  const setActiveStep = useSetRecoilState(activeStepSelector);

  const frontNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('front')
  );
  const windshieldNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('windshield')
  );
  const driversideNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('driverside')
  );
  const rearNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('rear')
  );
  const passengersideNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('passengerside')
  );

  const roofLeftNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('roofLeft')
  );

  const roofRightNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('roofRight')
  );

  const backDriversideAngleNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('backDriversideAngle')
  );

  const backPassengersideAngleNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('backPassengersideAngle')
  );

  const frontDriversideAngleNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('frontDriversideAngle')
  );

  const frontPassengersideAngleNumberOfDamages = useRecoilValue(
    numberOfDamagesPerStepSelector('frontPassengersideAngle')
  );

  const summaryDataConfig: SummaryConfigType[] = [
    {
      titleTranslationKey: 'photoSummaryFront',
      numberOfDamages: frontNumberOfDamages,
      onClickParam: 'front',
    },
    {
      titleTranslationKey: 'photoSummaryWindshieldAndRoof',
      numberOfDamages: windshieldNumberOfDamages,
      onClickParam: 'windshield',
    },
    {
      titleTranslationKey: 'photoSummaryFrontDriversideAngle',
      numberOfDamages: frontDriversideAngleNumberOfDamages,
      onClickParam: 'frontDriversideAngle',
    },
    {
      titleTranslationKey: 'photoSummaryDriverSide',
      numberOfDamages: driversideNumberOfDamages,
      onClickParam: 'driverside',
    },
    {
      titleTranslationKey: 'photoSummaryRoofLeft',
      numberOfDamages: roofLeftNumberOfDamages,
      onClickParam: 'roofLeft',
    },
    {
      titleTranslationKey: 'photoSummaryBackDriversideAngle',
      numberOfDamages: backDriversideAngleNumberOfDamages,
      onClickParam: 'backDriversideAngle',
    },
    {
      titleTranslationKey: 'photoSummaryRear',
      numberOfDamages: rearNumberOfDamages,
      onClickParam: 'rear',
    },
    {
      titleTranslationKey: 'photoSummaryBackPassengersideAngle',
      numberOfDamages: backPassengersideAngleNumberOfDamages,
      onClickParam: 'backPassengersideAngle',
    },
    {
      titleTranslationKey: 'photoSummaryPassengerSide',
      numberOfDamages: passengersideNumberOfDamages,
      onClickParam: 'passengerside',
    },
    {
      titleTranslationKey: 'photoSummaryRoofRight',
      numberOfDamages: roofRightNumberOfDamages,
      onClickParam: 'roofRight',
    },
    {
      titleTranslationKey: 'photoSummaryFrontPassengersideAngle',
      numberOfDamages: frontPassengersideAngleNumberOfDamages,
      onClickParam: 'frontPassengersideAngle',
    },
  ];

  const summaryData: SummaryDataType[] = summaryDataConfig.map(
    ({ titleTranslationKey, onClickParam, numberOfDamages }) => {
      return {
        title: t(`vsga:damagePhoto.${titleTranslationKey}`),
        info:
          numberOfDamages > 0
            ? t('vsga:summary.xDamages', { count: numberOfDamages })
            : t('vsga:summary.noDamage'),
        onClick: () => setActiveStep(onClickParam),
      };
    }
  );

  return (
    <InfoSummary
      summaryData={summaryData}
      atom={confirmationOfCorrectPhotoDataSelector}
      validationSelector={confirmationOfCorrectPhotoDataValidationSelector}
      flexDirection="column-reverse"
      finalCheck
      notice={t('vsga:damagePhoto.notice')}
    />
  );
}
